import React, { FC, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { OnboardingHighlightOverlay } from './OnboardingHighlightOverlay'
import { OnboardingTooltip } from './OnboardingTooltip'
import { trackEventMixpanel } from '@/utils/mixpanel'
import {
  OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_COMPLETED,
  OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_SKIPPED,
  OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_STEP_COMPLETED,
  OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED,
  OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_COMPLETED,
  OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_SKIPPED,
  OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_STEP_COMPLETED,
  OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED,
} from '@/utils/mixpanel/eventNames'
import {
  IOpenTextLessonTutorialStepConfig,
  OpenTextLessonTutorialStep,
} from '@/types/redux'
import openTextLessonTutorialSelector from '@/redux/selectors/open-text-lesson-onboarding-tutorial'
import {
  advanceOpenTextLessonTutorialStep,
  completeCurrentStep,
  hideOpenTextLessonTutorialInfoModal,
  setOpenTextLessonTutorialHighlightedElement,
  setOpenTextLessonTutorialLoading,
  showOpenTextLessonTutorialInfoModal,
  stopOpenTextLessonTutorial,
} from '@/redux/slices/open-text-lesson-onboarding-tutorial'

const initialStepConfigs: Partial<
  Record<OpenTextLessonTutorialStep, IOpenTextLessonTutorialStepConfig>
> = {
  [OpenTextLessonTutorialStep.SwitchToLearningMode]: {
    step: OpenTextLessonTutorialStep.SwitchToLearningMode,
    title: 'Learning Mode',
    description:
      'Try our new Learning Mode to reinforce your knowledge and get some extra practice!',
    highlightConfig: {
      elementId: 'lesson-learning-mode-tab-button',
    },
    nextStep: OpenTextLessonTutorialStep.LearningModeNoPenaltyDescription,
    actionRequired: true, // Wait for user to close tooltip
    autoAdvance: false,
  },
  [OpenTextLessonTutorialStep.LearningModeNoPenaltyDescription]: {
    step: OpenTextLessonTutorialStep.LearningModeNoPenaltyDescription,
    title: 'No Limitations!',
    description:
      "In Learning Mode, your progress won't be affected, so feel free to experiment.",
    highlightConfig: {
      elementId: 'visely-score-element',
    },
    nextStep: OpenTextLessonTutorialStep.SwitchToAssessmentMode,
    actionRequired: true, // Wait for user to close tooltip
    autoAdvance: false,
  },
  [OpenTextLessonTutorialStep.SwitchToAssessmentMode]: {
    step: OpenTextLessonTutorialStep.SwitchToAssessmentMode,
    title: 'Assessment Mode',
    description:
      'When you are ready, switch to Assessment Mode to get scored on your performance.',
    highlightConfig: {
      elementId: 'lesson-assessment-mode-tab-button',
    },
    nextStep: null,
    actionRequired: true, // Wait for user to close tooltip
    autoAdvance: false,
  },
}

interface OpenTextLessonTutorialProps {
  onComplete?: () => Promise<void>
  isDemoLesson?: boolean
  stepConfigs?: Partial<
    Record<OpenTextLessonTutorialStep, IOpenTextLessonTutorialStepConfig>
  >
}

export const OpenTextLessonTutorial: FC<OpenTextLessonTutorialProps> = ({
  onComplete,
  isDemoLesson = false,
  stepConfigs = initialStepConfigs,
}) => {
  const dispatch = useDispatch()
  const {
    isActive,
    currentStep,
    highlightedElementId,
    showInfoModal: showModal,
    infoModalContent,
    isLoading,
  } = useSelector(openTextLessonTutorialSelector)

  // Helper function to track step events
  const trackStepEvent = useCallback(
    (eventName: string, additionalProps = {}) => {
      const stepConfig = stepConfigs[currentStep]

      trackEventMixpanel(eventName, {
        'Tutorial Type': isDemoLesson
          ? 'Open Text Demo Lesson'
          : 'Open Text Lesson',
        'Step Name': currentStep,
        'Step Title': stepConfig?.title || 'Unknown',
        'Step Index': Object.keys(stepConfigs).indexOf(currentStep) + 1,
        'Total Steps': Object.keys(stepConfigs).length,
        'Action Required': stepConfig?.actionRequired || false,
        'Has Highlight': !!stepConfig?.highlightConfig,
        ...additionalProps,
      })
    },
    [currentStep, stepConfigs, isDemoLesson],
  )

  // Handle step progression logic
  useEffect(() => {
    console.log('[OPEN-TEXT-LESSON-ONBOARDING] Step progression useEffect:', {
      isActive,
      currentStep,
    })

    if (!isActive) {
      console.log(
        '[OPEN-TEXT-LESSON-ONBOARDING] Tutorial not active, skipping step progression',
      )
      return
    }

    const config = stepConfigs[currentStep]
    if (!config) {
      console.log(
        '[OPEN-TEXT-LESSON-ONBOARDING] No config found for step:',
        currentStep,
      )
      return
    }

    console.log('[OPEN-TEXT-LESSON-ONBOARDING] Processing step:', config.title)

    // Prepare modal content
    const modalContent = {
      title: config.title,
      description: config.description,
      // Don't show Next button for steps that require user interaction
      nextButtonText: config.nextStep ? 'Ok' : 'Complete',
      skipButtonText:
        currentStep !== OpenTextLessonTutorialStep.SwitchToAssessmentMode
          ? 'Skip Tutorial'
          : undefined,
    }

    // Set highlighted element if specified, then show modal
    if (config.highlightConfig) {
      // First hide modal and clear highlight to prevent content flickering
      dispatch(hideOpenTextLessonTutorialInfoModal())
      dispatch(setOpenTextLessonTutorialHighlightedElement(null))

      const elementRenderDelay = 500 // Extra time for lesson explorer

      // Wait a bit for the element to be rendered, then set highlight and show modal
      setTimeout(() => {
        const elementId = config.highlightConfig!.elementId

        // Regular step - show highlight and modal simultaneously
        dispatch(setOpenTextLessonTutorialHighlightedElement(elementId))
        console.log(
          '[OPEN-TEXT-LESSON-ONBOARDING] Dispatching showInfoModal with content:',
          modalContent,
        )
        dispatch(showOpenTextLessonTutorialInfoModal(modalContent))

        // Track step viewed
        trackStepEvent(
          isDemoLesson
            ? OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED
            : OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED,
          {
            'Element ID': elementId,
            'Element Type': 'Interactive Element',
          },
        )
      }, elementRenderDelay)
    } else {
      // No highlight needed, show modal immediately
      dispatch(setOpenTextLessonTutorialHighlightedElement(null))
      console.log(
        '[OPEN-TEXT-LESSON-ONBOARDING] Dispatching showInfoModal with content:',
        modalContent,
      )
      dispatch(showOpenTextLessonTutorialInfoModal(modalContent))

      // Track step viewed for non-highlight steps
      trackStepEvent(
        isDemoLesson
          ? OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED
          : OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_STEP_VIEWED,
        {
          'Element ID': null,
          'Element Type': 'Modal Only',
        },
      )
    }

    // Auto-advance if configured
    if (config.autoAdvance) {
      const timer = setTimeout(() => {
        const nextConfig = stepConfigs[currentStep]
        if (nextConfig?.nextStep) {
          dispatch(advanceOpenTextLessonTutorialStep(nextConfig.nextStep))
        }
      }, 3000) // Auto-advance after 3 seconds

      return () => clearTimeout(timer)
    }
  }, [
    isActive,
    currentStep,
    dispatch,
    trackStepEvent,
    isDemoLesson,
    stepConfigs,
  ])

  const handleComplete = useCallback(async () => {
    try {
      dispatch(setOpenTextLessonTutorialLoading(true))

      // Track tutorial completed
      trackStepEvent(
        isDemoLesson
          ? OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_COMPLETED
          : OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_COMPLETED,
        {
          'Completion Method': 'Complete Button',
          'Total Steps Completed': Object.keys(stepConfigs).length,
          'Tutorial Duration': 'Unknown', // Could track this with timestamp if needed
        },
      )

      dispatch(stopOpenTextLessonTutorial()) // Stop the tutorial instead of advancing to Completed

      if (onComplete) {
        await onComplete()
      }
    } catch (error) {
      console.error('Error completing tutorial:', error)
    } finally {
      dispatch(setOpenTextLessonTutorialLoading(false))
    }
  }, [dispatch, trackStepEvent, isDemoLesson, stepConfigs, onComplete])

  const handleNextStep = useCallback(
    async (trackingProps = {}) => {
      if (!currentStep) {
        return
      }

      const config = stepConfigs[currentStep]

      // Track step completed with provided tracking properties
      config &&
        trackStepEvent(
          isDemoLesson
            ? OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_STEP_COMPLETED
            : OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_STEP_COMPLETED,
          {
            'Next Step': config.nextStep || 'Complete',
            ...trackingProps,
          },
        )

      if (!config?.nextStep) {
        await handleComplete()
      } else {
        dispatch(completeCurrentStep())
      }
    },
    [
      currentStep,
      stepConfigs,
      trackStepEvent,
      isDemoLesson,
      handleComplete,
      dispatch,
    ],
  )
  const handleSkipTutorial = useCallback(
    async (skipMethod = 'Skip Button') => {
      try {
        dispatch(setOpenTextLessonTutorialLoading(true))

        // Track tutorial skipped with the provided method
        trackStepEvent(
          isDemoLesson
            ? OPEN_TEXT_DEMO_LESSON_ONBOARDING_TUTORIAL_SKIPPED
            : OPEN_TEXT_LESSON_ONBOARDING_TUTORIAL_SKIPPED,
          {
            'Skip Method': skipMethod,
            'Skipped At Step': currentStep,
            ...(currentStep
              ? {
                  'Steps Completed':
                    Object.keys(stepConfigs).indexOf(currentStep),
                }
              : {}),
          },
        )

        dispatch(stopOpenTextLessonTutorial())

        if (onComplete) {
          await onComplete()
        }
      } catch (error) {
        console.error('Error completing tutorial:', error)
      } finally {
        dispatch(setOpenTextLessonTutorialLoading(false))
      }
    },
    [
      dispatch,
      trackStepEvent,
      isDemoLesson,
      currentStep,
      stepConfigs,
      onComplete,
    ],
  )

  // Handle user clicking on highlighted elements - non-blocking approach
  useEffect(() => {
    if (!isActive || !highlightedElementId) return

    const config = stepConfigs[currentStep]
    if (!config?.actionRequired) return

    const handleElementClick = (event: Event) => {
      const target = event.target as HTMLElement
      const highlightedElement = document.getElementById(highlightedElementId)

      if (
        highlightedElement &&
        (highlightedElement.contains(target) || highlightedElement === target)
      ) {
        // Don't prevent default - let the user's action proceed naturally
        // Just advance the tutorial after a short delay
        console.log(
          '[OPEN-TEXT-LESSON-ONBOARDING] User clicked on highlighted element, advancing tutorial',
        )

        // Add extra delay for final lesson step to let lesson load
        const delay = 300

        setTimeout(() => {
          handleNextStep({
            'Completion Method': 'Element Click',
            'Element ID': highlightedElementId,
            'Element Type': target.tagName.toLowerCase(),
          })
        }, delay) // Longer delay for lesson loading, shorter for other steps
      }
    }

    // Use capture phase but don't prevent default
    document.addEventListener('click', handleElementClick, true)
    return () => {
      document.removeEventListener('click', handleElementClick, true)
    }
  }, [isActive, highlightedElementId, currentStep, handleNextStep, stepConfigs])

  if (!isActive) {
    return null
  }

  return (
    <>
      <OnboardingHighlightOverlay
        targetElementId={highlightedElementId}
        isActive={isActive}
        padding={8}
        zIndex={9999}
      />
      <OnboardingTooltip
        targetElementId={highlightedElementId}
        isOpen={showModal}
        content={infoModalContent}
        onNext={
          infoModalContent?.nextButtonText
            ? () => handleNextStep({ 'Completion Method': 'Next Button' })
            : undefined
        }
        onSkip={
          infoModalContent?.skipButtonText
            ? () => handleSkipTutorial('Skip Button')
            : undefined
        }
        onClose={() => handleSkipTutorial('Close Button (X)')}
        isLoading={isLoading}
        showBackdrop={false}
      />
    </>
  )
}
